import type { FilterDropdownProps } from 'antd/es/table/interface'

import { CheckOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Input } from 'antd'
import { useState } from 'react'
import { useDebounce } from 'react-use'

interface SelectOption {
  value: string
  label: React.ReactNode
}

interface Props<T> {
  queryKey: string
  mapper: (value: T) => SelectOption
  fetch: (searchQuery: string | undefined) => Promise<T[]>
  filterDropdownProps: FilterDropdownProps
}

// This is now a proper React component that accepts all props
export const TableSelectFilterComponent = <T,>(props: Props<T>): JSX.Element => {
  const { filterDropdownProps } = props
  const [searchQuery, setSearchQuery] = useState('')
  const [searchQueryDebounced, setSearchQueryDebounced] = useState('')

  useDebounce(
    () => {
      setSearchQueryDebounced(searchQuery)
    },
    500,
    [searchQuery]
  )

  const query = useQuery(
    ['users', searchQueryDebounced],
    async () => {
      return props.fetch(searchQueryDebounced)
    },
    {
      enabled: filterDropdownProps.visible,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000
    }
  )

  const options = query.data?.map<SelectOption>(props.mapper) ?? []

  return (
    <div>
      <div className='p-2 border-b'>
        <Input
          placeholder='Search by name'
          value={searchQuery}
          onChange={({ currentTarget }) => {
            setSearchQuery(currentTarget.value)
          }}
          allowClear
        />
      </div>

      <div className='overflow-y-auto max-h-60'>
        {options.map(option => (
          <div
            key={option.value}
            className='p-2 cursor-pointer hover:bg-gray-100'
            onClick={() => {
              const currentSelected = filterDropdownProps.selectedKeys as string[]
              const isSelected = currentSelected.includes(option.value)

              if (isSelected) {
                filterDropdownProps.setSelectedKeys(
                  currentSelected.filter(id => id !== option.value)
                )
              } else {
                filterDropdownProps.setSelectedKeys([...currentSelected, option.value])
              }
            }}
          >
            <div className='flex justify-between items-center'>
              <div>
                <div className='font-medium'>{option.label}</div>
              </div>
              {(filterDropdownProps.selectedKeys as string[]).includes(option.value) && (
                <CheckOutlined className='text-lucent-primary' />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className='flex gap-2 justify-end p-2 border-t'>
        <Button
          onClick={() => {
            filterDropdownProps.clearFilters?.()
            filterDropdownProps.confirm()
          }}
        >
          Reset
        </Button>

        <Button
          onClick={() => {
            filterDropdownProps.confirm()
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

// Higher-order function that returns a function (not a component with hooks)
// This is the original API that other code expects
export const TableSelectFilter = <T,>(props: Omit<Props<T>, 'filterDropdownProps'>) => {
  // This function doesn't use hooks - it just returns JSX
  return (filterDropdownProps: FilterDropdownProps): JSX.Element => {
    return <TableSelectFilterComponent {...props} filterDropdownProps={filterDropdownProps} />
  }
}
